import { useEffect, useId, useRef, useState } from 'react';

/**
 * A suggestion from a reviewed source (an EPN template, for example) shown BESIDE a field, never inside it. Nothing here writes to the field
 * until the person clicks "Use suggested ...".
 *
 *  - If the field is empty (or already equals the suggestion) the click inserts it.
 *  - If the field already has different text, the click opens "Replace what you wrote?" with **Keep mine** as the default (it takes focus,
 *    and Escape also keeps it) and **Replace**. Text the person typed is never overwritten without that second, explicit click.
 *  - The suggestion text is shown as suggestion text, not as a placeholder, so it can never be mistaken for a value or saved by accident.
 */

export interface SuggestionBoxProps {
  /** The field's current text. */
  current: string;
  /** The suggested text. When it is blank, nothing is rendered. */
  suggestion: string;
  /** "template" wording: "Suggested from the Payroll Specialist template". */
  source: string;
  /** What is suggested, used in the button: "summary" gives "Use suggested summary". */
  noun: string;
  /** Called with the suggestion only after the explicit click (and the confirmation, when needed). */
  onUse: (text: string) => void;
}

export function SuggestionBox({ current, suggestion, source, noun, onUse }: SuggestionBoxProps) {
  const uid = useId();
  const [confirming, setConfirming] = useState(false);
  const keepRef = useRef<HTMLButtonElement>(null);
  const useRef_ = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (confirming) keepRef.current?.focus();
  }, [confirming]);

  // A new suggestion (another template) starts fresh.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConfirming(false);
  }, [suggestion]);

  if (!suggestion.trim()) return null;
  const same = current.trim() === suggestion.trim();

  function use() {
    if (current.trim() && !same) {
      setConfirming(true);
      return;
    }
    onUse(suggestion);
  }

  // Already in the field (for example the template was just copied in): a single quiet line, not a second copy of the text.
  if (same) return <p className="jr-hint" role="status" data-testid={`suggestion-${noun.replace(/\s+/g, '-')}`}>The suggested {noun} from {source} is in the field. You can edit it.</p>;
  return (
    <div className="jr-suggestion" role="group" aria-labelledby={`${uid}-title`} data-testid={`suggestion-${noun.replace(/\s+/g, '-')}`}>
      <p id={`${uid}-title`} className="jr-suggestion__title">Suggested from {source}</p>
      <blockquote className="jr-suggestion__text">{suggestion}</blockquote>
      {confirming ? (
        <div className="jr-inline-confirm" role="group" aria-label={`Replace what you wrote?`} onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); setConfirming(false); window.setTimeout(() => useRef_.current?.focus(), 0); } }}>
          <p><strong>Replace what you wrote?</strong> The {noun} field already has text. Replacing it cannot be undone from here.</p>
          <div className="jr-actions">
            <button ref={keepRef} type="button" className="jr-btn jr-btn--primary jr-btn--small" onClick={() => { setConfirming(false); window.setTimeout(() => useRef_.current?.focus(), 0); }}>
              Keep mine
            </button>
            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => { setConfirming(false); onUse(suggestion); }}>
              Replace
            </button>
          </div>
        </div>
      ) : (
        <button ref={useRef_} type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={use}>Use suggested {noun}</button>
      )}
    </div>
  );
}
